import { RequestTypeEnum } from "@nlc/shared-types";
import { WrenchIcon } from "lucide-react";
import { Controller } from "react-hook-form";

import { Label } from "@/components/field";
import { FieldDisplay } from "@/components/field-display/FieldDisplay.tsx";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import {
  joinTags,
  ReadOnlyTags,
  splitTags,
  TagInput,
} from "@/components/tag-input";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess.tsx";
import { CollapsibleBox } from "@/features/request/edit/components/CollapsibleBox";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm";
import { useTemplateLock } from "@/features/request/edit/hooks/useTemplateLock.tsx";
import { AiAssetsToggle } from "@/features/request/shared/components/AiAssetsToggle";
import {
  CREATIVE_DIRECTION_OPTIONS,
  FILE_TYPE_SUGGESTIONS,
  SIZE_SUGGESTIONS,
} from "@/features/shared/constants/technicalDetailsConstants.ts";
import { useGetRequest } from "@/hooks/useGetRequest";

import { useFieldUpdate } from "../../hooks/useFieldUpdate";
import { PreferredDesignersSelect } from "./PreferedDesignersSelect";
import { VideoTechnicalFields } from "./VideoTechnicalFields";

export function TechnicalDetailsSection() {
  const methods = useEditRequestFormContext();
  const { debouncedUpdateField, updateField } = useFieldUpdate();
  const { data: request } = useGetRequest();
  const { canAccess } = useRoleAccess();

  const isVideo = request?.requestType === RequestTypeEnum.VIDEO;
  const isWebsiteImplementation =
    request?.requestType === RequestTypeEnum.WEBSITE_IMPLEMENTATION;

  const { isLocked } = useTemplateLock();
  const isReadOnly =
    canAccess("edit-request.details.designer.view") || isLocked;

  return (
    <CollapsibleBox
      icon={<WrenchIcon className="text-white" size={16} />}
      iconClassName="bg-orange-500"
      title="Technical details"
    >
      {isReadOnly ? (
        <ReadOnlyTechnicalDetails isVideo={isVideo} />
      ) : (
        <>
          {!isWebsiteImplementation && (
            <>
              <AiAssetsToggle
                control={methods.control}
                name="restrictAiAssets"
                onValueChange={(value) => {
                  void updateField("restrictAiAssets", value);
                }}
              />

              <Controller
                control={methods.control}
                name="creativeDirection"
                render={({ field: { name, onBlur, onChange, value } }) => (
                  <Select
                    className="w-full"
                    name={name}
                    onBlur={onBlur}
                    onSelectionChange={(id) => {
                      onChange(id);
                      debouncedUpdateField(name, id);
                    }}
                    placeholder="Select creative direction"
                    selectedKey={value}
                  >
                    <Label>Creative direction</Label>
                    <SelectButton />
                    <SelectPopover>
                      <SelectListBox>
                        {CREATIVE_DIRECTION_OPTIONS.map((option) => (
                          <SelectListItem id={option.id} key={option.id}>
                            {option.label}
                          </SelectListItem>
                        ))}
                      </SelectListBox>
                    </SelectPopover>
                  </Select>
                )}
              />

              <Controller
                control={methods.control}
                name="sizesNeeded"
                render={({
                  field: { name, onBlur, onChange, value },
                  fieldState: { error, invalid },
                }) => (
                  <TagInput
                    description="To add a new File size, input in WxH format, and press enter, or space, or separate by comma."
                    errorMessage={error?.message}
                    isInvalid={invalid}
                    label="Sizes needed"
                    name={name}
                    onBlur={onBlur}
                    onChange={(tags) => {
                      const next = joinTags(tags);

                      onChange(next);
                      debouncedUpdateField(name, next);
                    }}
                    placeholder="E.g. 1080x1080, 16x9, etc."
                    requiredHint
                    suggestions={SIZE_SUGGESTIONS}
                    value={splitTags(value)}
                  />
                )}
              />

              {isVideo ? <VideoTechnicalFields /> : null}

              <Controller
                control={methods.control}
                name="preferredFileType"
                render={({
                  field: { name, onBlur, onChange, value },
                  fieldState: { error, invalid },
                }) => (
                  <TagInput
                    description="Enter a new file type by pressing enter, or space, or separate by comma."
                    errorMessage={error?.message}
                    isInvalid={invalid}
                    label="File types"
                    name={name}
                    onBlur={onBlur}
                    onChange={(tags) => {
                      const next = joinTags(tags);

                      onChange(next);
                      debouncedUpdateField(name, next);
                    }}
                    placeholder="E.g. PNG, MP4, PDF, etc."
                    requiredHint
                    suggestions={
                      isVideo
                        ? FILE_TYPE_SUGGESTIONS[RequestTypeEnum.VIDEO]
                        : FILE_TYPE_SUGGESTIONS[RequestTypeEnum.GRAPHIC]
                    }
                    value={splitTags(value)}
                  />
                )}
              />
            </>
          )}

          <PreferredDesignersSelect />
        </>
      )}
    </CollapsibleBox>
  );
}

function ReadOnlyTechnicalDetails({ isVideo }: { isVideo: boolean }) {
  const { watch } = useEditRequestFormContext();

  const restrictAiAssets = watch("restrictAiAssets");
  const creativeDirection = watch("creativeDirection");
  const sizesNeeded = watch("sizesNeeded");
  const preferredFileType = watch("preferredFileType");
  const lengthOrPlatform = watch("lengthOrPlatform");
  const styleOfVideo = watch("styleOfVideo");
  const musicGenre = watch("musicGenre");

  const creativeDirectionLabel =
    CREATIVE_DIRECTION_OPTIONS.find((option) => option.id === creativeDirection)
      ?.label ?? creativeDirection;

  return (
    <div className="grid grid-cols-2 gap-3xl">
      <FieldDisplay
        label="Allow usage of AI-generated assets?"
        value={restrictAiAssets ? "No" : "Yes"}
      />
      <FieldDisplay
        label="Creative direction"
        value={creativeDirectionLabel || "Not specified"}
      />
      <FieldDisplay
        label="Sizes needed"
        value={<ReadOnlyTags value={sizesNeeded} />}
      />
      <FieldDisplay
        label="File types"
        value={<ReadOnlyTags value={preferredFileType} />}
      />
      {isVideo && (
        <>
          <FieldDisplay
            label="Length or platform"
            value={lengthOrPlatform || "Not specified"}
          />
          <FieldDisplay
            label="Style of video"
            value={styleOfVideo || "Not specified"}
          />
          <FieldDisplay
            label="Music genre"
            value={musicGenre || "Not specified"}
          />
        </>
      )}
    </div>
  );
}
